import React, { useCallback, useEffect, useState } from 'react';
import styles from './VolumeController.module.scss';
import classes from 'classnames';
import { AudioQueueManager, AudioQueueManagerEvents } from '@mavatar/melingo-voice-and-avatar-sdk';

const MIN_VOLUME = 0.1;
const MAX_VOLUME = 1;

interface VolumeControllerProps {
  audioQueueManager?: AudioQueueManager | null;
  layout?: 'line' | 'button';
}

const VolumeController = ({ audioQueueManager, layout = 'line' }: VolumeControllerProps) => {
  const [muted, setMuted] = useState<boolean>(false);
  const [volume, setVolume] = useState<number>(1);

  useEffect(() => {
    const onMutedChange = () => {
      const muted = audioQueueManager?.muted || false;
      setMuted(muted);
    };
    onMutedChange();

    const onVolumeChange = () => {
      const volume = audioQueueManager ? audioQueueManager.volume : 1;
      setVolume(volume);
    };
    onVolumeChange();

    audioQueueManager?.dispatcher.addListener(AudioQueueManagerEvents.MUTED_CHANGE, onMutedChange);
    audioQueueManager?.dispatcher.addListener(
      AudioQueueManagerEvents.VOLUME_CHANGE,
      onVolumeChange
    );

    return () => {
      audioQueueManager?.dispatcher.removeListener(
        AudioQueueManagerEvents.MUTED_CHANGE,
        onMutedChange
      );
      audioQueueManager?.dispatcher.removeListener(
        AudioQueueManagerEvents.VOLUME_CHANGE,
        onVolumeChange
      );
    };
  }, [audioQueueManager]);

  const onMutedToggle = useCallback(() => {
    if (audioQueueManager) {
      audioQueueManager.muted = !audioQueueManager.muted;
    }
  }, [audioQueueManager]);

  const onVolumeChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (audioQueueManager) {
        let volume = Number(event.target.value);
        volume = volume < MIN_VOLUME ? MIN_VOLUME : volume > MAX_VOLUME ? MAX_VOLUME : volume;
        audioQueueManager.volume = volume;
      }
    },
    [audioQueueManager]
  );

  return (
    <div className={classes(styles.container, styles[`layout-${layout}`])}>
      <button onClick={onMutedToggle} className={styles.mutBtn}>
        <svg
          viewBox='0 0 512 512'
          className={classes({
            [styles.volume1]: volume >= 0.1,
            [styles.volume2]: volume >= 0.5,
            [styles.volume3]: volume >= MAX_VOLUME,
            [styles.mute]: muted,
          })}
        >
          <path className={styles.muteLine} />
          <path className={classes(styles.speakerPart, styles.speakerPart1)} />
          <path className={classes(styles.speakerPart, styles.speakerPart2)} />
          <path className={classes(styles.saundLine, styles.saundLine1)} />
          <path className={classes(styles.saundLine, styles.saundLine2)} />
          <path className={classes(styles.saundLine, styles.saundLine3)} />
        </svg>
      </button>
      <input
        className={styles.volumeRange}
        type='range'
        min={MIN_VOLUME}
        max={MAX_VOLUME}
        step={0.1}
        value={volume}
        onChange={onVolumeChange}
      />
    </div>
  );
};

export default VolumeController;
